import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";

import { AuthActionCreators, IApplicationState } from "../";
import { IMatchHistory, IRecipient, IDonor, ICompatibilityResult, ILkdpiResults, IContact, IDonorPreselectNote, IListItem, ICrossMatchRejectionReason, IState, ISerologyTest, IBaseResponse, ISingleEpletMmRisk, IMatchDeadline, IMatchOfferDeclineRootCause, IMatchOfferDeclineReasonListItem, IMatchOfferDeclineChoices } from '../../models';
import { ICenterAddress } from '@nkr/models/center-address';
import { MatchHistoryType } from '@nkr/enums/match-history-type';

export interface IMatchResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            matchHistory: IMatchHistory,
            recipient: IRecipient,
            donor: IDonor,
            compatibilityResult: ICompatibilityResult,
            lkdpiResults: ILkdpiResults,
            compatiblePairLkdpiResults: ILkdpiResults,
            singleEpletMmRisk: ISingleEpletMmRisk,
            isRecipientCenter: boolean,
            isDonorCenter: boolean,
            donorNote: IDonorPreselectNote,
            matchNote: IDonorPreselectNote,
            donorKidneyPickupAddress: ICenterAddress,
            donorKidneyShipToAddress: ICenterAddress,
            recipientKidneyPickupAddress: ICenterAddress,
            recipientKidneyShipToAddress: ICenterAddress,
            serologyTestList: ISerologyTest[],
            pmp: string,
            currentDeclineRatio: number,
            newDeclineRatio: number,
            doesStudyExist: number,
            swapNumber: string,
            lookupData: {
                matchOfferDeclineRootCauseList: IMatchOfferDeclineRootCause[],
                matchOfferRejectionReasonList: IListItem[],
                crossMatchRejectionReasonList: ICrossMatchRejectionReason[],
                hardBlockReasons: IListItem[],
                stateList: IState[],
                recipientCenterContactList: IContact[],
                donorCenterContactList: IContact[],
            }
        }
    }
}

export interface IFinancialAgreement {
    swapLabel: string
    donorCenter: string
    recipientCenter: string
    donorBillingContactName: string
    recipientBillingContactName: string
    donorBillingContactPhoneNumber: string
    recipientBillingContactPhoneNumber: string
    donorBillingAddress: string
    recipientBillingAddress: string
    donorBillingCsz: string
    recipientBillingCsz: string
    donorFinancialContactName: string
    donorFinancialContactPhone: string
    donorFinancialContactEmail: string
    donorFinancialContactFax: string
    donorFinancialContactBackupName: string
    donorFinancialContactBackupPhone: string
    donorFinancialContactBackupEmail: string
    donorFinancialContactBackupFax: string
    recipientFinancialContactName: string
    recipientFinancialContactPhone: string
    recipientFinancialContactEmail: string
    recipientFinancialContactFax: string
    recipientFinancialContactBackupName: string
    recipientFinancialContactBackupPhone: string
    recipientFinancialContactBackupEmail: string
    recipientFinancialContactBackupFax: string
    donorAlias: string
    donorDob: string
    donorName: string
    donorAddress: string
    donorCsz: string
    recipientAlias: string
    recipientName: string
    recipientDob: string
    recipientAddress: string
    recipientCsz: string
    insurance: string
    insurance2: string
    medicare: string
    medicare2: string
    globalCaseRate: string
    proCaseRate: string
    policyNumber: string
    subscriber: string
    groupId: string
    insuranceLastUpdated: string
    claimsAddress: string
    caseManagerName: string
}

export interface IEvaluateRecipientDonorResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            recipient: IRecipient,
            donor: IDonor,
            compatibilityResult: ICompatibilityResult,
            lkdpiResults: ILkdpiResults,
            compatiblePairLkdpiResults: ILkdpiResults,
            singleEpletMmRisk: ISingleEpletMmRisk,
            isRecipientCenter: boolean,
            recipientCenterContacts: IContact[],
            isDonorCenter: boolean,
            donorCenterContacts: IContact[],
            donorNote: IDonorPreselectNote,
            matchNote: IDonorPreselectNote,
            donorKidneyPickupAddress: ICenterAddress,
            donorKidneyShipToAddress: ICenterAddress,
            recipientKidneyPickupAddress: ICenterAddress,
            recipientKidneyShipToAddress: ICenterAddress,
            serologyTestList: ISerologyTest[],
            doesStudyExist: number,
            hasExistingXm: boolean,
            existingXmStatus: string,
            hasOutstandingOffer: boolean,
            outstandingOfferId: number,
            swapNumber: string,
            rexm: {
                fullyConfident: boolean,
                centerResponses: number
            },
            lookupData: {
                stateList: IState[],
                hardBlockReasons: IListItem[]
            }
        }
    }
}

export interface IMatchOfferAccept {
    isFullyConfident: boolean,
    whichKidney: string,
    lateReason: string,
    additionalComments: string
}

export interface IMatchOfferDecline {
    blockFutureMatches: boolean,
    deactivatePair: boolean,
    lateReason: string,
    rejectionReason: number,
    rejectionReasonText: string,
    rootCause: number,
    additionalComments: string,
    isSurgeryDateAcceptable: boolean,
    deactivatePatient: boolean,
    declineReasons: { id: number, otherText: string, nextAvailText: string }[]
}

export interface ICrossMatchAccept {
    isDonorReviewedSatisfactorily: boolean,
    additionalComments: string,
    requiresFreshBlood?: boolean,
    serologyBloodDrawDate?: string,
    finalXmBloodDrawDate?: string,
    requiresSerology?: boolean
}

export interface ICrossMatchDecline {
    blockFutureMatches: boolean,
    deactivatePair: boolean,
    lateReason: string,
    rootCausePrimary: number,
    rootCauseSecondary: number,
    rejectionReasonText: string,
    measuresToTake: string
}

export interface IMatchDeadlineListResponse {
    data: {
        success: boolean,
        message: string,
        results: IMatchDeadline[]
    }
}

export interface IMatchOfferDecisionReasonListResponse {
    data: {
        success: boolean,
        reasonList: IMatchOfferDeclineReasonListItem[]
    }
}

export const ActionCreators = {
    getMatch: (matchHistoryId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IMatchResponse> => {
        let options = {
            url: `${__API__}/match-history/evaluate-offer/${matchHistoryId}?centerId=${centerId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getRecipientDonor: (recipientId: number, donorId: number, centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IEvaluateRecipientDonorResponse> => {
        var options = {
            url: `${__API__}/match-history/evaluate-recipient-donor?recipientId=${recipientId}&donorId=${donorId}&centerId=${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IEvaluateRecipientDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    getRexm: (centerId: number, recipientId: number, rexmId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IEvaluateRecipientDonorResponse> => {
        var options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/rexm/${rexmId}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IEvaluateRecipientDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    makeDecision: (
        matchHistoryId: number, centerId: number, initials: string,
        matchOfferAccept: IMatchOfferAccept, matchOfferDecline: IMatchOfferDecline, crossMatchAccept: ICrossMatchAccept,
        crossMatchDecline: ICrossMatchDecline,
        matchOfferDeclineChoices: IMatchOfferDeclineChoices) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
            var data: any = null;

            if (matchOfferAccept != null) {
                data = {
                    matchHistoryId,
                    centerId,
                    isAccepted: true,
                    isFullyConfident: matchOfferAccept.isFullyConfident,
                    whichKidney: matchOfferAccept.whichKidney,
                    lateReason: matchOfferAccept.lateReason,
                    additionalComments: matchOfferAccept.additionalComments,
                    signOff: initials
                };
            }

            if (matchOfferDecline != null) {
                data = {
                    matchHistoryId,
                    centerId,
                    isAccepted: false,
                    blockDonor: matchOfferDeclineChoices.isDonorAcceptable == false,
                    deactivatePair: matchOfferDecline.deactivatePatient,
                    lateReason: matchOfferDecline.lateReason,
                    rejectionReason: null,
                    rejectionReasonText: matchOfferDecline.rejectionReasonText,
                    rootCause: null,
                    additionalComments: matchOfferDecline.additionalComments,
                    signOff: initials,
                    isDonorAcceptable: matchOfferDeclineChoices.isDonorAcceptable,
                    isSurgeryDateAcceptable: matchOfferDecline.isSurgeryDateAcceptable,
                    deactivatePatient: matchOfferDecline.deactivatePatient,
                    declineReasons: matchOfferDecline.declineReasons
                };
            }

            if (crossMatchAccept != null)
                data = {
                    matchHistoryId,
                    centerId,
                    isAccepted: true,
                    isDonorReviewedSatisfactorily: crossMatchAccept.isDonorReviewedSatisfactorily,
                    lateReason: '',
                    additionalComments: crossMatchAccept.additionalComments,
                    requiresFreshBlood: crossMatchAccept.requiresFreshBlood,
                    serologyBloodDrawDate: crossMatchAccept.serologyBloodDrawDate,
                    finalXmBloodDrawDate: crossMatchAccept.finalXmBloodDrawDate,
                    requiresSerology: crossMatchAccept.requiresSerology,
                    signOff: initials
                };

            if (crossMatchDecline != null)
                data = {
                    matchHistoryId,
                    centerId,
                    isAccepted: false,
                    blockDonor: crossMatchDecline.blockFutureMatches,
                    deactivatePair: crossMatchDecline.deactivatePair,
                    lateReason: crossMatchDecline.lateReason,
                    rejectionReason: crossMatchDecline.rootCauseSecondary,
                    rejectionReasonText: crossMatchDecline.rejectionReasonText,
                    measuresToTake: crossMatchDecline.measuresToTake,
                    signOff: initials
                };

            var options = {
                url: `${__API__}/match-history/make-decision`,
                data
            };

            return dispatch(AuthActionCreators.apiPost(options))
                .then((response: IBaseResponse) => {
                    return Promise.resolve(response);
                })
                .catch((error: AxiosError) => {
                    throw error;
                });
        },
    acceptDonor: (recipientId: number, donorId: number, isFullyConfident: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/donor-preselect/accept`,
            data: {
                recipientId,
                donorId,
                isFullyConfident
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    declineDonor: (recipientId: number, donorId: number, hardBlockSource: string, hardBlockReason: string, hardBlockComments: string, isGlobalDecline: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/donor-preselect/decline`,
            data: {
                recipientId,
                donorId,
                hardBlockSource,
                hardBlockReason,
                hardBlockComments,
                isGlobalDecline
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    donorInMatch: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/match-history/check-donor-in-match`,
            data: {
                donorId,
                recipientId
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    getByType: (type: MatchHistoryType) => async (dispatch: Dispatch<IApplicationState>): Promise<IMatchDeadlineListResponse> => {
        let options = {
            url: `${__API__}/match-history/by-type?type=${type}`,
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    setNoResponse: (id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/match-history/${id}/set-no-response`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    updateDeadlineById: (id: number, deadline: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/match-history/${id}/update-deadline?deadline=${deadline}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    getMatchOfferDeclineReasons: (matchHistoryId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IMatchOfferDecisionReasonListResponse> => {
        let options = {
            url: `${__API__}/match-history/match-offer-decline-reasons/${matchHistoryId}`,
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateBillableCenter: (donorId: number, recipientId: number, reversalResponsible: number, prereversalId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/match-history/update-billable-center`,
            data: {
                donorId,
                recipientId,
                reversalResponsible,
                prereversalId
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
}

export interface IMatchState {
}

var initialState: IMatchState = {
};

const createReducer = (initialState: IMatchState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
};

export const Reducer = createReducer(initialState, handlers);
